(() => {
  const SUBSCRIPTION_HANDLES = new Set([
    'calmi-monthly-subscription',
    'calmi-annual-subscription',
    'calmi-premium-monthly-test',
    'calmi-premium-annual-test',
    'calmi-wellness-app'
  ]);
  const TRIAL_DEVICE_KEY = 'calmi_trial_device_id';
  const ELIGIBILITY_ENDPOINT =
    'https://us-central1-calmi-680d4.cloudfunctions.net/shopifyTrialEligibility';
  const productCache = new Map();
  let trialEligiblePromise;

  const routesRoot = () => window.Shopify?.routes?.root || '/';

  const requestJson = async (path, options = {}) => {
    const response = await fetch(`${routesRoot()}${path}`, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? {'Content-Type': 'application/json'} : {}),
        ...options.headers
      }
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload.description || `Cart request failed: ${response.status}`);
    }

    return payload;
  };

  const getDeviceId = () => {
    try {
      let value = localStorage.getItem(TRIAL_DEVICE_KEY) || '';
      if (!value) {
        value = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(TRIAL_DEVICE_KEY, value);
      }
      return value;
    } catch (_error) {
      return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  };

  const isSubscriptionItem = (item) => {
    const properties = item.properties || {};
    return Boolean(item.selling_plan_allocation) && (
      properties._calmi_subscription === 'true' ||
      SUBSCRIPTION_HANDLES.has(String(item.handle || ''))
    );
  };

  const getProduct = async (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(handle, requestJson(`products/${handle}.js`));
    }
    return productCache.get(handle);
  };

  const getPlan = async (planType, eligible) => {
    const handle = planType === 'annual'
      ? 'calmi-annual-subscription'
      : 'calmi-monthly-subscription';
    const product = await getProduct(handle);
    const variant = product.variants?.[0];
    const plans = product.selling_plan_groups?.flatMap((group) => group.selling_plans || []) || [];
    const trialPlan = plans.find((plan) => /free trial/i.test(plan.name));
    const paidPlan = plans.find((plan) => !/free trial/i.test(plan.name));
    const sellingPlan = eligible ? (trialPlan || paidPlan) : (paidPlan || trialPlan);

    if (!variant?.id || !sellingPlan?.id) throw new Error('Subscription plan is unavailable');

    return {handle, variantId: Number(variant.id), sellingPlan, isTrial: sellingPlan === trialPlan};
  };

  const checkTrialEligibility = () => {
    if (!trialEligiblePromise) {
      trialEligiblePromise = fetch(ELIGIBILITY_ENDPOINT, {
        method: 'POST',
        headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
        body: JSON.stringify({
          customerId: '',
          email: '',
          deviceId: getDeviceId(),
          guestCheckout: true
        })
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Eligibility request failed: ${response.status}`);
          return response.json();
        })
        .then((result) => result.eligible !== false)
        .catch((error) => {
          console.error('Calmi trial eligibility check failed', error);
          return false;
        });
    }
    return trialEligiblePromise;
  };

  const refreshCart = async () => {
    const cart = await requestJson('cart.js');
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {resource: cart, sourceId: 'calmi-subscription-cart'}
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {cart, openMiniCart: true}
    }));
    return cart;
  };

  const normalizeSubscriptionQuantities = async () => {
    const cart = await requestJson('cart.js');
    const updates = Object.fromEntries(
      (cart.items || [])
        .filter((item) => isSubscriptionItem(item) && Number(item.quantity) !== 1)
        .map((item) => [item.key, 1])
    );
    if (!Object.keys(updates).length) return;
    await requestJson('cart/update.js', {method: 'POST', body: JSON.stringify({updates})});
    await refreshCart();
  };

  const lockSubscriptionQuantityControls = () => {
    document.querySelectorAll('[data-product-handle]').forEach((line) => {
      if (!SUBSCRIPTION_HANDLES.has(line.dataset.productHandle || '')) return;
      const selector = line.querySelector('.quantity-selector');
      if (!selector) return;

      const input = selector.querySelector('input[name="updates[]"]');
      if (input) {
        input.value = '1';
        input.max = '1';
        input.readOnly = true;
        input.setAttribute('aria-label', 'Subscription quantity is limited to one');
      }

      const increase = selector.querySelector('[aria-label*="increase" i]');
      if (increase) {
        increase.removeAttribute('href');
        increase.setAttribute('aria-disabled', 'true');
        increase.style.pointerEvents = 'none';
        increase.style.opacity = '0.35';
      }
    });
  };

  const renderSubscriptionCopy = (wrapper, eligible) => {
    const button = wrapper?.querySelector('[data-calmi-plan-button]');
    const summary = wrapper?.querySelector('[data-calmi-plan-summary]');
    const selected = wrapper?.querySelector('[data-calmi-plan]:checked');
    if (!button || !selected) return;

    const interval = selected.value === 'annual' ? 'year' : 'month';
    button.textContent = eligible ? 'Start free trial' : 'Add subscription to cart';
    if (summary) {
      summary.textContent = eligible
        ? `Try 7 days free, then ${selected.dataset.price}/${interval}. Cancel anytime.`
        : `${selected.dataset.price}/${interval}. Cancel anytime.`;
    }
  };

  const updateSubscriptionButton = async () => {
    const wrapper = document.querySelector('[data-calmi-plans]');
    const button = wrapper?.querySelector('[data-calmi-plan-button]');
    if (!button) return;

    button.disabled = true;
    button.textContent = 'Checking trial eligibility...';
    const eligible = await checkTrialEligibility();
    button.disabled = false;
    wrapper.dataset.calmiTrialEligible = String(eligible);
    renderSubscriptionCopy(wrapper, eligible);
  };

  document.addEventListener('change', (event) => {
    const selected = event.target.closest?.('[data-calmi-plan]');
    const wrapper = selected?.closest('[data-calmi-plans]');
    if (!wrapper || !('calmiTrialEligible' in wrapper.dataset)) return;
    renderSubscriptionCopy(wrapper, wrapper.dataset.calmiTrialEligible === 'true');
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-calmi-plan-button]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const wrapper = button.closest('[data-calmi-plans]');
    const error = wrapper?.querySelector('[data-calmi-plan-error]');
    const selected = wrapper?.querySelector('[data-calmi-plan]:checked');
    if (!wrapper || !selected) return;

    error.hidden = true;
    button.disabled = true;
    button.textContent = 'Adding to cart...';

    try {
      const eligible = wrapper.dataset.calmiTrialEligible === 'true'
        ? true
        : await checkTrialEligibility();
      const plan = await getPlan(selected.value, eligible);
      const cart = await requestJson('cart.js');
      const subscriptionItems = (cart.items || []).filter(isSubscriptionItem);
      const matchingItem = subscriptionItems.find((item) =>
        Number(item.variant_id) === plan.variantId &&
        String(item.selling_plan_allocation?.selling_plan?.id || '') === String(plan.sellingPlan.id)
      );

      if (matchingItem && subscriptionItems.length === 1) {
        if (Number(matchingItem.quantity) !== 1) {
          await requestJson('cart/change.js', {
            method: 'POST',
            body: JSON.stringify({id: matchingItem.key, quantity: 1})
          });
        }
      } else {
        if (subscriptionItems.length) {
          const updates = Object.fromEntries(subscriptionItems.map((item) => [item.key, 0]));
          await requestJson('cart/update.js', {method: 'POST', body: JSON.stringify({updates})});
        }
        await requestJson('cart/add.js', {
          method: 'POST',
          body: JSON.stringify({
            id: plan.variantId,
            quantity: 1,
            selling_plan: Number(plan.sellingPlan.id),
            properties: {
              _calmi_subscription: 'true',
              _calmi_plan_type: plan.isTrial ? 'trial' : 'paid',
              ...(plan.isTrial ? {
                _calmi_device_id: getDeviceId(),
                _calmi_guest_checkout: 'true'
              } : {})
            }
          })
        });
      }

      await requestJson('cart/update.js', {
        method: 'POST',
        body: JSON.stringify({attributes: {
          calmi_device_id: plan.isTrial ? getDeviceId() : '',
          calmi_guest_checkout: plan.isTrial ? 'true' : ''
        }})
      });
      await refreshCart();
      button.disabled = false;
      button.textContent = plan.isTrial ? 'Start free trial' : 'Add subscription to cart';
    } catch (error_) {
      console.error('Calmi subscription cart add failed', error_);
      button.disabled = false;
      button.textContent = 'Try again';
      error.textContent = 'We could not add the subscription to your cart. Please try again.';
      error.hidden = false;
    }
  }, true);

  const initialize = () => {
    lockSubscriptionQuantityControls();
    new MutationObserver(lockSubscriptionQuantityControls)
      .observe(document.body, {childList: true, subtree: true});
    normalizeSubscriptionQuantities().catch((error) => {
      console.error('Calmi subscription quantity normalization failed', error);
    });
    updateSubscriptionButton();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {once: true});
  } else {
    initialize();
  }
})();
